<template>
  <button
    class="scroll-top-progress"
    :class="{ 'is-visible': isVisible }"
    type="button"
    aria-label="بازگشت به بالای صفحه"
    title="بازگشت به بالای صفحه"
    @click="scrollToTop"
  >
    <svg class="scroll-top-progress-ring" viewBox="0 0 56 56" aria-hidden="true" focusable="false">
      <circle class="scroll-top-progress-track" cx="28" cy="28" r="24" />
      <circle
        class="scroll-top-progress-indicator"
        cx="28"
        cy="28"
        r="24"
        :style="{ strokeDashoffset: indicatorOffset }"
      />
    </svg>
    <font-awesome-icon icon="chevron-up" class="scroll-top-progress-icon" aria-hidden="true" />
  </button>
</template>

<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from "vue";

const SHOW_AT = 0.3;
const CIRCUMFERENCE = 2 * Math.PI * 24;
const scrollProgress = ref(0);
const isVisible = computed(() => scrollProgress.value >= SHOW_AT);
const indicatorOffset = computed(() => CIRCUMFERENCE * (1 - scrollProgress.value));

let ticking = false;
let pendingScrollTarget = window;

function updateProgress() {
  const source = pendingScrollTarget;
  const isScrollableElement = source instanceof Element && source.scrollHeight > source.clientHeight;

  if (isScrollableElement) {
    const maxScroll = Math.max(1, source.scrollHeight - source.clientHeight);
    scrollProgress.value = Math.min(1, Math.max(0, source.scrollTop / maxScroll));
    ticking = false;
    return;
  }

  const documentElement = document.documentElement;
  const body = document.body;
  const scrollHeight = Math.max(
    documentElement.scrollHeight,
    body?.scrollHeight || 0,
    documentElement.offsetHeight,
    body?.offsetHeight || 0,
  );
  const maxScroll = Math.max(1, scrollHeight - window.innerHeight);
  const currentScroll = window.pageYOffset || documentElement.scrollTop || body?.scrollTop || 0;

  scrollProgress.value = Math.min(1, Math.max(0, currentScroll / maxScroll));
  ticking = false;
}

function onScroll(event) {
  // The application uses scrollable content containers on several layouts.
  // Capture the event target so those containers drive the widget as well.
  if (event?.target instanceof Element) pendingScrollTarget = event.target;
  if (ticking) return;
  ticking = true;
  window.requestAnimationFrame(updateProgress);
}

function scrollToTop() {
  if (pendingScrollTarget instanceof Element && pendingScrollTarget.scrollHeight > pendingScrollTarget.clientHeight) {
    pendingScrollTarget.scrollTo({ top: 0, behavior: "smooth" });
    return;
  }
  window.scrollTo({ top: 0, behavior: "smooth" });
}

onMounted(() => {
  window.addEventListener("scroll", onScroll, { passive: true, capture: true });
  window.addEventListener("resize", onScroll);
  updateProgress();
});

onBeforeUnmount(() => {
  window.removeEventListener("scroll", onScroll, true);
  window.removeEventListener("resize", onScroll);
});
</script>

<style scoped>
.scroll-top-progress {
  --scroll-progress: 0;
  box-sizing: border-box;
  position: fixed;
  right: 24px;
  bottom: 50px;
  z-index: 9999;
  width: 56px;
  height: 56px;
  display: grid;
  place-items: center;
  padding: 0;
  border: 1px solid rgba(var(--text-color-secondary-rgb), 0.22);
  border-radius: 30px;
  background: var(--background-color);
  color: var(--text-color-primary);
  box-shadow: 0 10px 24px rgba(13, 24, 43, 0.22);
  cursor: pointer;
  opacity: 0;
  visibility: hidden;
  transform: translateY(14px) scale(0.94);
  transition: opacity 0.24s ease, visibility 0.24s ease, transform 0.24s ease,
    box-shadow 0.24s ease, background-color 0.3s ease, color 0.3s ease;
  overflow: hidden;
}

.scroll-top-progress.is-visible {
  opacity: 1;
  visibility: visible;
  transform: translateY(0) scale(1);
}

.scroll-top-progress:hover {
  color: var(--text-color-button);
  background: var(--background-color-button);
  box-shadow: 0 14px 28px rgba(13, 24, 43, 0.28);
  transform: translateY(-2px) scale(1.02);
}

.scroll-top-progress:focus-visible {
  outline: 2px solid var(--background-color-button);
  outline-offset: 3px;
}

.scroll-top-progress-ring {
  position: absolute;
  inset: 0;
  width: 56px;
  height: 56px;
  display: block;
  overflow: visible;
  transform: rotate(-90deg);
}

.scroll-top-progress-track,
.scroll-top-progress-indicator {
  fill: none;
  stroke-width: 4;
}

.scroll-top-progress-track {
  stroke: rgba(var(--text-color-secondary-rgb), 0.2);
}

.scroll-top-progress-indicator {
  stroke: var(--background-color-button);
  stroke-linecap: round;
  stroke-dasharray: 150.796;
  transition: stroke-dashoffset 0.12s linear, stroke 0.3s ease;
}

.scroll-top-progress-icon {
  position: relative;
  z-index: 1;
  width: 20px;
  height: 20px;
}

@media (max-width: 768px) {
  .scroll-top-progress {
    right: 16px;
    bottom: 50px;
    width: 52px;
    height: 52px;
  }

  .scroll-top-progress-ring {
    width: 52px;
    height: 52px;
  }
}
</style>
